import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { PaginatorModule } from 'primeng/paginator';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { FormScrapReasonsService } from '../../../../shared/components/form-scrap-reasons/form-scrap-reasons.service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { BehaviorSubject, combineLatest, forkJoin, Subject } from 'rxjs';
import { ScrapReason } from '../../../../api/elite/types/scrap-reason';
import { User } from '../../../../api/elite/types/users';
import { AlertService } from '../../../../core/services/alert.service';
import { FormScrapReasonsComponent } from '../../../../shared/components/form-scrap-reasons/form-scrap-reasons.component';
import { Shift } from '../../../../api/elite/types/shift';
import { FormScrapReasonData } from '../../../../shared/types/form-scrap-reasons';
import { SchedulerStageItemApiService } from '../../api/table.service';
import { ValidScrapOperator } from '../../interfaces/valid-scrap-operator.interface';
import { ValidMachine } from '../../interfaces/valid-machine.interface';
import { P } from '@angular/cdk/keycodes';

export type HosesInspectionRegisterDialogTypes = {
  data: {
    maxQuantity: number;
    title: string;
    department: string;
    operatorType?: string;
    currentOperatorName?: string;
  };
  return: {
    quantity: number;
    scrap: number;
    shiftId: number;
    scrapReasons: FormScrapReasonData[];
    operatorId: number;
    machineId: number;
    fecha: string;
  };
};

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    PaginatorModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    NgxMatSelectSearchModule,
    FormScrapReasonsComponent,
  ],
  templateUrl: './hoses-inspection-stage.component.html',
  styleUrls: ['./hoses-inspection-stage.component.sass'],
  providers: [FormScrapReasonsService],
})
export class HosesInspectionRegisterDialogComponent implements OnInit {
  private readonly data: HosesInspectionRegisterDialogTypes['data'] =
    inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef);
  private readonly apiService = inject(SchedulerStageItemApiService);
  private readonly alertService = inject(AlertService);
  private readonly formScrapReasonsService = inject(FormScrapReasonsService);
  private destroy$ = new Subject<void>();
  private _operators = new BehaviorSubject<ValidScrapOperator[]>([]);
  private _machines = new BehaviorSubject<ValidMachine[]>([]);
  maxQuantity = this.data.maxQuantity;

  form = new FormGroup({
    quantity: new FormControl<number>(undefined, [
      Validators.required,
      Validators.min(0),
      Validators.max(this.maxQuantity),
    ]),
    shiftId: new FormControl<number>(null, Validators.required),
    scrap: new FormControl<number>(0, [Validators.required, Validators.min(0)]),

    searchOperator: new FormControl<string>(''),
    operatorId: new FormControl<number>(undefined, Validators.required),

    searchMachine: new FormControl<string>(''),
    machineId: new FormControl<number>(undefined, Validators.required),

    fecha: new FormControl<string>(new Date().toISOString().substring(0, 10), Validators.required),
  });

  scrapReasons: ScrapReason[] = [];
  shifts: Shift[] = [];

  readonly operators$ = combineLatest([
    this._operators.asObservable(),
    this.form.get('searchOperator')?.valueChanges.pipe(
      startWith(''),
      map((data) => (data || '').toLowerCase())
    ),
  ]).pipe(
    map(([operators, search]) => {
      if (!search) return operators;
      return operators.filter(
        (op) =>
          op.fullName.toLowerCase().includes(search) ||
          op.number?.toString().toLowerCase().includes(search)
      );
    }),
    takeUntil(this.destroy$)
  );

  readonly machines$ = combineLatest([
    this._machines.asObservable(),
    this.form.get('searchMachine')?.valueChanges.pipe(
      startWith(''),
      map((data) => (data || '').toLowerCase())
    ),
  ]).pipe(
    map(([machines, search]) => {
      if (!search) return machines;
      return machines.filter(
        (m) =>
          m.title.toLowerCase().includes(search)
      );
    }),
    takeUntil(this.destroy$)
  );

  showReasons = false;

  get title(): string {
    return this.data.currentOperatorName || this.data.title;
  }

  get disable(): boolean {
    return this.data.maxQuantity === 0;
  }

  constructor() {}

  ngOnInit(): void {
    const transactionNumber = this.title;

    forkJoin({
      mainData: this.apiService.getData(this.data.department, this.data.operatorType),
      validOperators: this.apiService.getValidOperators(transactionNumber),
    }).subscribe({
      next: ({ mainData, validOperators }) => {
        this.scrapReasons = mainData.department.scrapReasons;
        this.shifts = mainData.department.shifts;
        this.formScrapReasonsService.scrapReasons = mainData.department.scrapReasons;
        this._operators.next(validOperators);
      },
      error: (err) => {
        console.error('Error loading data:', err);
        this.alertService.error('Error al cargar datos');
      }
    });

    this.onFormChange();
    this.setupDynamicMaxQuantity();
  }

  onFormChange(): void {
    this.form
      .get('scrap')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val: number) => {
        this.formScrapReasonsService.quantity = val;
        if (val <= 0) {
          this.formScrapReasonsService.resetForm();
        }
        this.showReasons = val > 0;
      });
  }

  onClickClose(): void {
    this.dialogRef.close(false);
  }

  onClickSave(): void {
    const { quantity, scrap, shiftId, operatorId, machineId, fecha } = this.form.value;

    const total = (scrap || 0) + (quantity || 0);

    if (total > this.maxQuantity) {
      this.alertService.error('Estás registrando más cantidad de la que tienes.');
      return;
    }
    if (total === 0) {
      this.alertService.error('No se puede registrar 0 piezas');
      return;
    }

    if (!this.formScrapReasonsService.isQuantityValid()) {
      this.alertService.error(`Las cantidades distribuidas en las razones de scrap no coinciden`);
      return;
    }
    if (!this.formScrapReasonsService.isFormValid() && scrap > 0) {
      this.alertService.error(`Formulario de scrap inválido`);
      return;
    }

    const response = {
      quantity,
      scrap,
      shiftId,
      operatorId,
      machineId,
      fecha,
      scrapReasons: this.showReasons ? this.formScrapReasonsService.getData() : undefined,
    };

    this.alertService.success('Datos validados correctamente');
    this.dialogRef.close(response);
  }

  private setupDynamicMaxQuantity(): void {
    this.form.get('operatorId').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedId: number) => {
        const operators: ValidScrapOperator[] = this._operators.value;
        const selectedOp = operators.find(op => op.id === selectedId);

        if (selectedOp) {
          this.maxQuantity = selectedOp.availableQuantity;

          this.form.get('machineId').setValue(undefined, { emitEvent: false });

          this.apiService.getValidMachines(this.title, selectedId).subscribe({
            next: (machines) => this._machines.next(machines),
            error: () => this.alertService.error('Error al cargar máquinas del operador')
          });

          this.applyQuantityValidators();

        }
      });

    this.form.get('machineId').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedMachineId: number) => {
        const machines = this._machines.value;
        const selectedMac = machines.find(m => m.id === selectedMachineId);

        if (selectedMac) {
          this.maxQuantity = selectedMac.availableQuantity;
        } else {
          const currentOpId = this.form.get('operatorId').value;
          const selectedOp = this._operators.value.find(op => op.id === currentOpId);
          this.maxQuantity = selectedOp ? selectedOp.availableQuantity : this.data.maxQuantity;
        }

        this.applyQuantityValidators();
      });
  }

  private applyQuantityValidators(): void {
    const quantityControl = this.form.get('quantity');
    quantityControl.setValidators([
      Validators.required,
      Validators.min(0),
      Validators.max(this.maxQuantity)
    ]);

    quantityControl.updateValueAndValidity();
  }
}
